import { apiGet, apiPost, apiPut, apiDelete } from './client';
import type { ApiResponse, PagedResponse, SearchParams } from './types';

// --- Workflow Step Definition ---

export interface WorkflowStepDefinition {
  step_index: number;
  name: string;
  type: string; // action, condition, delay, notification
  action: string; // send_email, update_field, create_task, webhook, etc.
  config: Record<string, unknown>;
  conditions: Record<string, unknown>;
  on_success: number | null;
  on_failure: number | null;
}

// --- Workflow Definition Document ---

export interface WorkflowDefinition {
  id: string;
  tenant_id: string;
  name: string;
  description: string;
  trigger_type: string; // manual, event, scheduled
  trigger_config: Record<string, unknown>;
  steps: WorkflowStepDefinition[];
  status: string; // active, inactive, draft
  version: number;
  tags: string[];
  branch_id: string;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
}

// --- Workflow Step Result ---

export interface WorkflowStepResult {
  step_index: number;
  step_name: string;
  status: string; // pending, running, completed, failed, skipped
  started_at: string | null;
  completed_at: string | null;
  output: Record<string, unknown>;
  error: string;
}

// --- Workflow Instance Document ---

export interface WorkflowInstance {
  id: string;
  tenant_id: string;
  definition_id: string;
  definition_name: string;
  trigger_data: Record<string, unknown>;
  status: string; // pending, running, completed, failed, cancelled
  current_step_index: number;
  step_results: WorkflowStepResult[];
  started_at: string | null;
  completed_at: string | null;
  error: string;
  branch_id: string;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
}

// --- Create/Update DTOs ---

export interface CreateWorkflowDefinitionDto {
  name: string;
  description?: string;
  trigger_type: string;
  trigger_config?: Record<string, unknown>;
  steps?: WorkflowStepDefinition[];
  tags?: string[];
}

export interface UpdateWorkflowDefinitionDto {
  name: string;
  description?: string;
  trigger_type: string;
  trigger_config?: Record<string, unknown>;
  steps?: WorkflowStepDefinition[];
  tags?: string[];
}

export interface CreateWorkflowInstanceDto {
  definition_id: string;
  trigger_data?: Record<string, unknown>;
}

// --- Filter Types ---

export interface WorkflowDefinitionFilters extends SearchParams {
  trigger_type?: string;
  status?: string;
}

export interface WorkflowInstanceFilters extends SearchParams {
  definition_id?: string;
  status?: string;
}

// ========== Workflow Definition API Functions ==========

export async function getWorkflowDefinitions(params?: WorkflowDefinitionFilters) {
  return apiGet<PagedResponse<WorkflowDefinition>>('/api/v1/workflow-definitions', params);
}

export async function getWorkflowDefinition(id: string) {
  return apiGet<WorkflowDefinition>(`/api/v1/workflow-definitions/${id}`);
}

export async function createWorkflowDefinition(data: CreateWorkflowDefinitionDto) {
  return apiPost<WorkflowDefinition>('/api/v1/workflow-definitions', data);
}

export async function updateWorkflowDefinition(id: string, data: UpdateWorkflowDefinitionDto) {
  return apiPut<WorkflowDefinition>(`/api/v1/workflow-definitions/${id}`, data);
}

export async function deleteWorkflowDefinition(id: string) {
  return apiDelete<void>(`/api/v1/workflow-definitions/${id}`);
}

export async function activateWorkflowDefinition(id: string) {
  return apiPost<WorkflowDefinition>(`/api/v1/workflow-definitions/${id}/activate`);
}

export async function deactivateWorkflowDefinition(id: string) {
  return apiPost<WorkflowDefinition>(`/api/v1/workflow-definitions/${id}/deactivate`);
}

// ========== Workflow Instance API Functions ==========

export async function getWorkflowInstances(params?: WorkflowInstanceFilters) {
  return apiGet<PagedResponse<WorkflowInstance>>('/api/v1/workflow-instances', params);
}

export async function getWorkflowInstance(id: string) {
  return apiGet<WorkflowInstance>(`/api/v1/workflow-instances/${id}`);
}

export async function createWorkflowInstance(data: CreateWorkflowInstanceDto) {
  return apiPost<WorkflowInstance>('/api/v1/workflow-instances', data);
}

export async function deleteWorkflowInstance(id: string) {
  return apiDelete<void>(`/api/v1/workflow-instances/${id}`);
}

export async function cancelWorkflowInstance(id: string) {
  return apiPost<WorkflowInstance>(`/api/v1/workflow-instances/${id}/cancel`);
}

export async function executeWorkflowStep(id: string) {
  return apiPost<WorkflowInstance>(`/api/v1/workflow-instances/${id}/execute-step`);
}
